import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { raisedColor } from '../detailSurfaces'
import { isBlank, EMPTY_DASH } from '../detailShared'

// §2.2 — el cero PRESENTE se pinta, en gris y sin resaltar: es un dato medido, no un problema. Solo
// un valor positivo toma el color que le corresponde.
const figureTone = (value, positiveTone) => (Number(value) >= 1 ? positiveTone : 'text.secondary')

/**
 * ¿Hay algo rechazado que merezca el rojo? (ADDENDUM 33 / E39, punto 1)
 *
 * Dos valores caen del mismo lado por razones distintas: la AUSENCIA llega como `''` (RN8, el dato
 * no ha llegado, que no es cero) y el CERO es un cero medido. Ninguno de los dos es un rechazo.
 * `Number('')` es 0, así que la misma comparación cubre los dos.
 *
 * 📌 ANOTADO PORQUE UN SABOTAJE LO DESTAPÓ: esto empezó siendo una guarda de cuatro ramas contra
 * `null`, `undefined` y cadenas no numéricas, y cambiarla por un `!!value` pelado NO tumbaba ningún
 * test. El sabotaje verde acusaba al predicado, no al test: `rejected` es un `Integer` en Sun
 * (`QuantityBreakdown.java:20`) y el mapeador ya convierte el nulo en `''`, así que los únicos
 * valores que pueden llegar aquí son `''` o un entero — y sobre ese rango `!!value` y esto dicen lo
 * mismo. Las otras ramas defendían de entradas imposibles, que es cobertura que nadie puede probar.
 * Si algún día `rejected` pasa a ser fraccionario, ESTA línea es la que hay que volver a mirar.
 */
const hasRejections = (value) => Number(value) >= 1

// LAS TRES TARJETAS GIGANTES SE FUNDEN EN UNA FRANJA (nota n2 del PO, §5.3).
//
// Eran tres cajas con 20 px de relleno, borde propio y un rótulo en versalitas encima de cada
// cifra: el ancho entero del bloque para decir tres números que no cambian mientras se mira la
// pantalla. La franja dice los mismos tres con la MISMA cifra grande y un tercio del alto, y el
// sitio que libera se lo queda lo que sí se viene a leer — los hitos y la tabla.
//
// ⚠ ADDENDUM 33 (E39) 1 SIGUE EN PIE, y es lo único de la tarjeta vieja que había que conservar: el
// rojo lo decide EL VALOR, no la posición. `tinted` llega calculado desde fuera, igual que antes, y
// una orden sin una sola pieza mala no se lee en rojo.
//
// ⚠️ §2.2, LAS DOS MITADES DE LA MISMA REGLA: un CERO PRESENTE se pinta —en gris, sin resaltar— y
// una AUSENCIA no se deja en blanco nunca, sale como la raya larga en ámbar. La tarjeta vieja
// pintaba `value` a pelo: con la cifra ausente la caja salía MUDA, con su rótulo encima de un
// hueco, que es lo que RN8 prohíbe. Se veía poco porque las tres suelen faltar a la vez —y ese caso
// tiene su propia frase—, pero basta con que Sun sirva una sola de las tres para que ocurra.
const SummaryFigure = ({ testId, label, value, tone, tinted }) => {
  const missing = isBlank(value)
  return (
    <Box
      data-testid={testId}
      sx={(theme) => ({
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'baseline',
        gap: 1,
        px: 2.5,
        py: 1.5,
        backgroundColor: tinted ? alpha(theme.palette.error.main, 0.08) : 'transparent'
      })}
    >
      {/* §2.4 — toda cifra en monoespaciada: es lo que alinea las tres y separa el dato del rótulo. */}
      <Typography data-testid={`${testId}-value`} sx={{ fontSize: 32, fontWeight: 800, lineHeight: 1, fontFamily: 'monospace', color: missing ? 'warning.main' : tone }}>
        {missing ? EMPTY_DASH : value}
      </Typography>
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>{label}</Typography>
    </Box>
  )
}

// El separador vertical de la franja. Un `<Divider>` de MUI en `flex` necesita `flexItem` y trae su
// propio color; esto es una línea de 1 px con el mismo borde que el marco, que es todo lo que hace
// falta para que las tres cifras se lean como tres y no como una.
const FigureDivider = () => (
  <Box sx={(theme) => ({ width: '1px', alignSelf: 'stretch', backgroundColor: alpha(theme.palette.text.primary, 0.08) })} />
)

// The three figures of the order (reported good, rejected, left to manufacture), or the sentence that
// says there is nothing reported yet.
const ProgressSummaryStrip = ({ summary, noOperationStarted }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const noProduction = isBlank(summary.good) && isBlank(summary.rejected) && isBlank(summary.remaining)

  return (
    <Box data-testid='progress-summary' sx={{ mb: 3 }}>
      {noProduction
        ? (
          <Box>
            <Typography data-testid='summary-no-reports' variant='body2' sx={{ color: 'text.secondary' }}>{t_('No reports yet')}</Typography>
            {noOperationStarted && (
              <Typography data-testid='summary-no-operation-started' variant='body2' sx={{ color: 'text.secondary' }}>{t_('No operation has started yet')}</Typography>
            )}
          </Box>
          )
        : (
          <Box
            data-testid='progress-summary-strip'
            sx={{ display: 'flex', alignItems: 'stretch', borderRadius: 2, border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`, backgroundColor: raisedColor(theme), overflow: 'hidden' }}
          >
            {/* El rótulo baja a la DERECHA de la cifra y en minúsculas. Encima y en versalitas
            competía con el número por la primera lectura; al lado, la cifra manda y el
            rótulo la explica, que es el orden en que se lee «26 reportadas OK». */}
            <SummaryFigure testId='summary-good' label={t_('Reported good')} value={summary.good} tone={figureTone(summary.good, 'text.primary')} />
            <FigureDivider />
            <SummaryFigure testId='summary-rejected' label={t_('Rejected')} value={summary.rejected} tone={figureTone(summary.rejected, 'error.main')} tinted={hasRejections(summary.rejected)} />
            <FigureDivider />
            {/* Lo que queda por fabricar es el dato «en curso» de la franja, y toma el teal de
            información: no es bueno ni malo, es lo que falta. */}
            <SummaryFigure testId='summary-remaining' label={t_('Left to manufacture')} value={summary.remaining} tone={figureTone(summary.remaining, 'info.main')} />
          </Box>
          )}
    </Box>
  )
}

export default ProgressSummaryStrip
